import { track } from '@vercel/analytics';

import { AnalyticsBase } from '../base';
import { AnalyticsID } from '../constants';
import type { VercelAnalyticsSink } from '../types';

const trackCookieEventServerSide = (
  event: 'display' | 'accept' | 'decline',
) => {
  fetch(`/api/track/cookies?event=${event}`, {
    method: 'POST',
    keepalive: true,
  }).catch(() => {});
};

export class VercelAnalytics
  extends AnalyticsBase
  implements VercelAnalyticsSink
{
  id = AnalyticsID.Vercel;

  constructor() {
    super();
    this.isInitialized = true;
  }

  events = {
    cookiesDisplay() {
      trackCookieEventServerSide('display');
    },

    cookiesAccept() {
      trackCookieEventServerSide('accept');
    },

    cookiesDecline() {
      trackCookieEventServerSide('decline');
    },

    cartUpsellDisplay({ type, product }) {
      track(`cart/upsell_${type}`, { product });
    },

    cartUpsellAccepted({ type, product, variant }) {
      track(`cart/upsell_${type}`, { product, variant: variant || null });
    },

    searchClick({ query, path, title, position, results }) {
      track('search/click', { query, path, title, position, results });
    },

    searchQuery({ query, results }) {
      track('search/query', { query, results });
    },

    aboutVideoPlay() {
      track('about/video_play');
    },

    overlayPopupDisplay() {
      track('overlayPopup/display');
    },

    overlayPopupClick({ url }) {
      track('overlayPopup/click', { url });
    },

    overlayPopupClose() {
      track('overlayPopup/close');
    },

    quizStart() {
      track('skincare_quiz/start');
    },

    quizAnswer({ question, answer }) {
      track(`skincare_quiz/answer_${question}`, { answer });
    },

    quizComplete() {
      track('skincare_quiz/complete');
    },

    quizResults({ product }) {
      track('skincare_quiz/results', { product });
    },

    quizResultsBundle() {
      track('skincare_quiz/results_bundle');
    },

    quizResultsATC({ product }) {
      track('skincare_quiz/results_atc', { product });
    },

    quizResultsShare() {
      track('skincare_quiz/results_share');
    },

    siteToggleSkincareClick() {
      track('site_toggle/skincare_click');
    },

    siteToggleSupplementsClick() {
      track('site_toggle/supplements_click');
    },

    siteToggleCalloutDisplay() {
      track('site_toggle/callout_display');
    },

    siteToggleCalloutClose() {
      track('site_toggle/callout_close');
    },

    siteToggleCalloutClick() {
      track('site_toggle/callout_click');
    },

    skincareCardDisplay({ slug }) {
      track('skincare_card/display', { slug });
    },

    skincareCardClick({ slug, url }) {
      track('skincare_card/click', { slug, url });
    },

    popupBannerDisplay({ title, url }) {
      track('popup_banner/display', { title, url });
    },

    popupBannerClick({ title, url }) {
      track('popup_banner/click', { title, url });
    },

    popupBannerClose({ title, url }) {
      track('popup_banner/close', { title, url });
    },

    freegiftShowProgressBar() {
      track('cart/freegift_showProgressBar');
    },

    freegiftShowChoices({ product }) {
      track('cart/freegift_showChoices', { product });
    },

    freegiftAccept({ product }) {
      track('cart/freegift_accept', { product });
    },

    longevityMonthView({ day }) {
      track('longevity_month/view', { day });
    },

    longevityMonthPartnershipClick({ day, url }) {
      track('longevity_month/partnership_click', { day, url });
    },

    longevityMonthShareInsta({ day }) {
      track('longevity_month/share_insta', { day });
    },

    longevityMonthShareTiktok({ day }) {
      track('longevity_month/share_tiktok', { day });
    },

    longevityMonthFollowInsta({ day }) {
      track('longevity_month/follow_insta', { day });
    },

    skincareHeroBadgeClick() {
      track('skincare_hero/badge_click');
    },

    videoPlay({ location }) {
      track(`${location}/video_play`);
    },

    videoMilestone({ location, milestone }) {
      track(`${location}/video_milestone`, { milestone });
    },

    hcpLinkClick({ source }) {
      track('hcp_link/click', { source });
    },
  } satisfies VercelAnalyticsSink['events'];
}
